import React from 'react';
import type { TemplateNavGroup } from '../hooks/useEffectiveSchema';
import { resolveIcon } from './common/IconResolver';

/**
 * Rendered nav group — matches the NavGroup shape Portal uses.
 * Icon is a React element (resolved from string name).
 */
export interface RenderedNavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  requiredRoles?: string[];    // role gating (WQ-TENANT-ADMIN-NAV)
  requiredFeature?: string;    // feature flag gating (WQ-076)
  requiredPlan?: string[];     // plan tier gating (WQ-076)
  items: RenderedNavItem[];
}

export interface RenderedNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  view?: string;               // view registry key
  requiredRoles?: string[];    // WQ-217: item-level role gating
  requiredFeature?: string;    // item-level feature gating (WQ-076)
  requiredPlan?: string[];     // item-level plan gating (WQ-076)
}

/**
 * Convert template navigation groups (JSON with string icon names)
 * to rendered nav groups (React elements for icons).
 */
export function buildNavGroups(
  templateGroups: TemplateNavGroup[]
): RenderedNavGroup[] {
  return templateGroups.map(group => ({
    id: group.id,
    label: group.label,
    icon: resolveIcon(group.icon, 18),
    ...(group.requiredRoles ? { requiredRoles: group.requiredRoles } : {}),
    ...(group.requiredFeature ? { requiredFeature: group.requiredFeature } : {}),
    ...(group.requiredPlan ? { requiredPlan: group.requiredPlan } : {}),
    items: group.items.map(item => ({
      id: item.id,
      label: item.label,
      icon: resolveIcon(item.icon, 18),
      view: item.view || 'stub',
      ...(item.requiredRoles ? { requiredRoles: item.requiredRoles } : {}),
      ...(item.requiredFeature ? { requiredFeature: item.requiredFeature } : {}),
      ...(item.requiredPlan ? { requiredPlan: item.requiredPlan } : {}),
    })),
  }));
}
